import React, { useState, useEffect, useMemo } from 'react';
import { Box, Grid, Alert, FormGroup, FormControlLabel, Checkbox, Typography } from '@mui/material';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import InvButton from '../../components/InvButton';
import { InvTextField, InvSelect } from '../../components/InvField';
import { generateConsolidation } from '../../api/consolidation';
import { getStores } from '../../api/stockLedger';
import { isRootStore } from '../../utils/storeEntity';
import { consolidationRowId } from '../../utils/consolidationEntity';
import useInvRole from '../../hooks/useInvRole';
import { INV_ACTIONS } from '../../utils/inventoryRoles';
import { IPD_COLORS } from '../../theme';

const STATUS_OPTIONS = ['SUBMITTED', 'APPROVED'];

function normalizeStores(data) {
  return Array.isArray(data) ? data : (data?.items || data?.data || []);
}

export default function ConsolidationForm() {
  const history = useHistory();
  const { can } = useInvRole();
  const { url } = useRouteMatch();
  const base = url.replace(/\/consolidations\/?$/, '');
  const [stores, setStores] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [statusIn, setStatusIn] = useState(['SUBMITTED', 'APPROVED']);

  const { control, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      central_store_id: '',
      from_date: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
      to_date: dayjs().format('YYYY-MM-DD'),
    },
  });

  useEffect(() => {
    getStores()
      .then(d => setStores(normalizeStores(d)))
      .catch(() => setStores([]));
  }, []);

  const centralStoreOptions = useMemo(
    () => stores
      .filter(s => isRootStore(s) && s.is_active !== false)
      .map(s => ({ value: s.store_id || s.id, label: `${s.store_name} (${s.store_code})` })),
    [stores],
  );

  // tenant_id/hospital_id aren't carried on the form itself — resolve them off the
  // selected central store's own record, same fallback used across the dispensing flows.
  function resolveStoreScope(storeId) {
    const store = stores.find(s => (s.store_id || s.id) === storeId);
    const scope = {};
    if (store?.tenant_id) scope.tenant_id = store.tenant_id;
    if (store?.hospital_id) scope.hospital_id = store.hospital_id;
    return scope;
  }

  function toggleStatus(status) {
    setStatusIn(prev => {
      if (prev.includes(status)) {
        const next = prev.filter(s => s !== status);
        return next.length ? next : prev;
      }
      return [...prev, status];
    });
  }

  async function onSubmit(data) {
    if (!can(INV_ACTIONS.CONSOLIDATION_CREATE)) return;
    if (statusIn.length === 0) {
      toast.error('Select at least one requisition status');
      return;
    }
    setGenerating(true);
    try {
      const result = await generateConsolidation({
        ...resolveStoreScope(data.central_store_id),
        central_store_id: data.central_store_id,
        from_date: data.from_date,
        to_date: data.to_date,
        requisition_status_in: statusIn,
      });
      const id = consolidationRowId(result);
      if (!id) {
        toast.error('Consolidation created but no ID returned from server');
        return;
      }
      toast.success('Consolidation generated — review shortfall items');
      history.push(`${base}/consolidations/${id}`);
    } catch (e) {
      const msg = e?.message || 'Failed to generate consolidation';
      if (/sufficient stock/i.test(msg)) {
        toast.info(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setGenerating(false);
    }
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <PageHeader
        eyebrow="Inventory Management › Requisitions"
        title="Requisition Consolidation"
        description="Aggregate sub-store requisitions, compare against central store stock, and build a procurement shortfall list."
        actions={
          <>
            <InvButton variant="outlined" tone="teal" onClick={() => history.push(`${base}/requisitions`)}>
              Requisitions
            </InvButton>
            <InvButton type="submit" loading={generating}>Generate Consolidation</InvButton>
          </>
        }
      />

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={8}>
          <SectionCard title="Consolidation Parameters">
            <Alert severity="info" sx={{ mb: 2, borderRadius: '10px' }}>
              Collects <strong>DEPARTMENT_REQUISITION</strong> transactions in the date range,
              sums quantities per item, and includes only items where central available stock is less than total required.
            </Alert>
            {centralStoreOptions.length === 0 && (
              <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px' }}>
                No parent (central) stores found. Create a parent store under Stock → Stores first.
              </Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="central_store_id"
                  control={control}
                  rules={{ required: 'Central store is required' }}
                  render={({ field }) => (
                    <InvSelect
                      label="Central Store *"
                      value={field.value}
                      onChange={field.onChange}
                      options={centralStoreOptions}
                      error={errors.central_store_id}
                      helperText="Parent / main store whose stock is checked against requisition demand"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="from_date"
                  control={control}
                  rules={{ required: 'Required' }}
                  render={({ field }) => (
                    <InvTextField
                      {...field}
                      label="From Date *"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      error={errors.from_date}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="to_date"
                  control={control}
                  rules={{ required: 'Required' }}
                  render={({ field }) => (
                    <InvTextField
                      {...field}
                      label="To Date *"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      error={errors.to_date}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: IPD_COLORS.textSecondary, mb: 0.5 }}>
                  Include Requisitions With Status
                </Typography>
                <FormGroup row>
                  {STATUS_OPTIONS.map(status => (
                    <FormControlLabel
                      key={status}
                      control={
                        <Checkbox
                          size="small"
                          checked={statusIn.includes(status)}
                          onChange={() => toggleStatus(status)}
                          sx={{ color: IPD_COLORS.primary, '&.Mui-checked': { color: IPD_COLORS.primary } }}
                        />
                      }
                      label={status.replace(/_/g, ' ')}
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                    />
                  ))}
                </FormGroup>
              </Grid>
            </Grid>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <SectionCard title="How It Works">
            <Box component="ol" sx={{ m: 0, pl: 2.5, color: IPD_COLORS.textSecondary, fontSize: 13, lineHeight: 1.7 }}>
              <li>Sub-stores create and approve requisitions</li>
              <li>System aggregates item quantities across requisitions</li>
              <li>Compares total demand vs central store available stock</li>
              <li>Builds a buy list for shortfall items only</li>
              <li>Optional: raise a PO linked to this consolidation</li>
            </Box>
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
}
